import { useEffect, useState } from 'react';

import { db, timestamp } from 'src/utils/firebase';

/**
 * Custom hook for managing liabilities in Firestore.
 * @param {string} userId - The user ID to fetch liabilities for.
 * @returns {object} An object containing liabilities array and CRUD operations.
 */
const useLiabilityService = (userId) => {
  const [liabilities, setLiabilities] = useState([]);

  useEffect(() => {
    if (!userId) {
      setLiabilities([]); // Set liabilities to an empty array when the user is not authenticated
      return () => {}; // Exit the useEffect early
    }

    const unsubscribe = db
      .collection('liabilities')
      .where('user_id', '==', userId)
      .onSnapshot((snapshot) => {
        console.log('liabilities updated...');
        const fetchedLiabilities = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setLiabilities(fetchedLiabilities);
      });

    return () => unsubscribe();
  }, [userId]);

  const sanitizeLiability = (liability) => ({
    ...liability,
    term: Number(liability.term),
    outstanding_balance: Number(liability.outstanding_balance),
    interest_rate_percentage: Number(liability.interest_rate_percentage),
    origination_principal_amount: Number(liability.origination_principal_amount),
  });

  const createLiability = (batch, liability) => {
    const newDocRef = db.collection('liabilities').doc();
    batch.set(newDocRef, {
      ...sanitizeLiability(liability),
      user_id: userId,
      created_at: timestamp(new Date()),
    });
  };

  const updateLiability = (batch, id, liability) => {
    const docRef = db.collection('liabilities').doc(id);
    batch.set(
      docRef,
      {
        ...sanitizeLiability(liability),
        updated_at: timestamp(new Date()),
      },
      { merge: true }
    );
  };

  const saveLiabilities = async (liabilitiesToUpdate) => {
    const batch = db.batch();

    if (typeof liabilitiesToUpdate === 'object' && !Array.isArray(liabilitiesToUpdate))
      liabilitiesToUpdate = [liabilitiesToUpdate];

    liabilitiesToUpdate.forEach((liability) => {
      if (liability.provider === 'manual') {
        if (liability.id) {
          // only write changes to manually managed liabilities
          updateLiability(batch, liability.id, liability);
        } else {
          createLiability(batch, liability);
        }
      }
    });

    try {
      // Commit the batch operation
      await batch.commit();
    } catch (error) {
      console.error('Failed to save liabilities', error.message);
    }

    return liabilitiesToUpdate;
  };

  const saveLiability = (liability) => saveLiabilities([liability]);

  return { liabilities, saveLiability, saveLiabilities };
};

export default useLiabilityService;
